import type { UsageResponse } from '../services/billingService';
import Company from '../interfaces/Company';

/** Must match backend libs/global_variables (enterprise conversion). */
export const CREDITS_PER_CALL_MINUTE = 2.5;
export const CREDITS_PER_CHAT_MESSAGE = 1;

function isEnterprisePlanUsage(usage: UsageResponse): boolean {
  if (usage.enterprise === true) return true;
  if ((usage.plan_tier ?? '').toLowerCase() === 'enterprise') return true;
  return (usage.plan_id ?? '').toLowerCase().includes('enterprise');
}

/** Show credit-pool UI when enterprise or bundle shares one monthly credit pool. */
export function showCreditPoolSection(usage: UsageResponse): boolean {
  if (
    usage.bundle_credit_pool === true ||
    usage.credit_pool === true ||
    isEnterprisePlanUsage(usage)
  ) {
    return true;
  }
  const cap = usage.monthly_credits_cap ?? usage.credits_limit;
  return cap != null && cap > 0;
}

/** Merge company billing fields when get_usage omits credit pool details. */
export function enrichUsageWithCompany(
  usage: UsageResponse | null,
  company: Company | null | undefined
): UsageResponse | null {
  if (!usage) return null;

  const companyCap =
    typeof company?.credits_limit === 'number' && company.credits_limit > 0
      ? company.credits_limit
      : undefined;

  const creditsCap =
    usage.monthly_credits_cap ?? usage.credits_limit ?? companyCap;

  const enterprise =
    usage.enterprise === true ||
    company?.enterprise === true ||
    isEnterprisePlanUsage(usage);

  let enriched: UsageResponse = {
    ...usage,
    enterprise,
    credits_limit: usage.credits_limit ?? creditsCap,
    monthly_credits_cap: usage.monthly_credits_cap ?? creditsCap,
  };

  if (creditsCap != null && creditsCap > 0) {
    const used =
      enriched.credits_used != null && Number.isFinite(enriched.credits_used)
        ? enriched.credits_used
        : resolvedCreditsUsed(enriched);
    const remaining =
      enriched.monthly_credits ??
      enriched.credits_remaining ??
      Math.max(0, creditsCap - used);

    enriched = {
      ...enriched,
      credits_used: enriched.credits_used ?? used,
      credits_remaining: enriched.credits_remaining ?? remaining,
      monthly_credits: enriched.monthly_credits ?? remaining,
    };
  }

  if (usage.overage_credits_limit === undefined) {
    enriched.overage_credits_limit = company?.overage_credits_limit ?? null;
  }
  if (usage.monthly_overage_credits_used === undefined) {
    enriched.monthly_overage_credits_used =
      company?.monthly_overage_credits_used;
  }

  return enriched;
}

/** Starter/business (paid) plans that may use metered overage after the included pool. */
export function isPaidMeteredPlan(usage: UsageResponse): boolean {
  if (usage.enterprise === true) return false;
  const tier = (usage.plan_tier ?? '').toLowerCase();
  if (tier === 'free' || tier === 'enterprise') return false;
  if (tier === 'starter' || tier === 'business') return true;
  const planId = usage.plan_id ?? '';
  return /^(call|chat|bundle)_(starter|business)/i.test(planId);
}

export function resolvedCreditsUsed(usage: UsageResponse): number {
  if (usage.credits_used != null && Number.isFinite(usage.credits_used)) {
    return usage.credits_used;
  }
  return (
    usage.minutes_used * CREDITS_PER_CALL_MINUTE +
    usage.messages_used * CREDITS_PER_CHAT_MESSAGE
  );
}

export function resolvedCreditsCap(usage: UsageResponse): number | null {
  const explicit = usage.monthly_credits_cap ?? usage.credits_limit;
  if (explicit != null && explicit > 0) return explicit;

  if (usage.minutes_limit != null && usage.minutes_limit > 0) {
    if (usage.plan_type === 'chat') {
      // Chat-only plans do not expose a minute cap.
    } else {
      return usage.minutes_limit * CREDITS_PER_CALL_MINUTE;
    }
  }
  if (
    usage.messages_limit != null &&
    usage.messages_limit > 0 &&
    usage.plan_type === 'chat'
  ) {
    return usage.messages_limit * CREDITS_PER_CHAT_MESSAGE;
  }
  if (
    usage.messages_limit != null &&
    usage.messages_limit > 0 &&
    usage.plan_type === 'bundle'
  ) {
    return usage.messages_limit * CREDITS_PER_CHAT_MESSAGE;
  }
  return null;
}

export function resolvedPoolRemaining(usage: UsageResponse): number | null {
  const fromApi = usage.monthly_credits ?? usage.credits_remaining;
  if (fromApi != null) return Math.max(0, fromApi);
  const cap = resolvedCreditsCap(usage);
  if (cap == null) return null;
  return Math.max(0, cap - resolvedCreditsUsed(usage));
}

/** Paid plan: included allowance used up; further usage is metered extra credits. */
export function isUsingExtraCredits(usage: UsageResponse): boolean {
  if (usage.using_extra_credits === true) return true;
  if (usage.usage_billing_mode === 'extra_credits') return true;
  if (!isPaidMeteredPlan(usage)) return false;
  if (usage.included_allowance_exhausted === true) return true;
  const remaining = resolvedPoolRemaining(usage);
  return remaining !== null && remaining <= 0;
}

/** Capped plan with no allowance left and no metered overage (e.g. free tier). */
export function isAllowanceExhaustedOnPlan(usage: UsageResponse): boolean {
  if (usage.usage_billing_mode === 'allowance_exhausted') return true;
  if (isUsingExtraCredits(usage)) return false;
  if (usage.enterprise === true || usage.usage_billing_mode === 'unlimited') {
    return false;
  }
  if (usage.included_allowance_exhausted === true) return true;
  const remaining = resolvedPoolRemaining(usage);
  return remaining !== null && remaining <= 0;
}

/** Consumption % for the shared monthly credit pool (0–100). */
export function enterpriseCreditsConsumptionPercent(
  usage: UsageResponse
): number {
  const cap = resolvedCreditsCap(usage);
  if (cap == null || cap <= 0) return 0;
  if (usage.credits_used != null && Number.isFinite(usage.credits_used)) {
    return Math.min(100, (usage.credits_used / cap) * 100);
  }
  const remaining = usage.monthly_credits ?? usage.credits_remaining;
  if (remaining != null && Number.isFinite(remaining)) {
    const consumed = Math.min(cap, Math.max(0, cap - remaining));
    return Math.min(100, (consumed / cap) * 100);
  }
  return Math.min(100, (resolvedCreditsUsed(usage) / cap) * 100);
}

export function formatEquivMinutes(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value >= 10 ? Math.round(value).toString() : value.toFixed(1);
}

export function formatEquivMessages(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return Math.floor(value).toString();
}

export function resolvedEquivMinutesRemaining(usage: UsageResponse): number {
  if (usage.equivalent_call_minutes_remaining != null) {
    return Number(usage.equivalent_call_minutes_remaining);
  }
  return (usage.monthly_credits ?? 0) / CREDITS_PER_CALL_MINUTE;
}

export function resolvedEquivMessagesRemaining(usage: UsageResponse): number {
  if (usage.equivalent_chat_messages_remaining != null) {
    return Number(usage.equivalent_chat_messages_remaining);
  }
  return (usage.monthly_credits ?? 0) / CREDITS_PER_CHAT_MESSAGE;
}

/** Rounded credits consumed this period for display chips. */
export function creditsUsedForDisplay(usage: UsageResponse): number {
  const used = resolvedCreditsUsed(usage);
  return Math.round(used * 10) / 10;
}

export function creditsCapForDisplay(usage: UsageResponse): number | null {
  return resolvedCreditsCap(usage);
}
